/**
 * BranchPublicPage — Página pública de agendamento de uma filial.
 * Rota: /filial/:branchSlug
 *
 * Carrega os dados da filial via trpc.branches.getPublic e renderiza
 * a página de agendamento usando o salonId do salão-sede.
 */
import { useParams, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useState, useMemo } from "react";
import { Loader2, Building2, AlertCircle, MapPin, Phone, Mail, Clock, Star, ChevronRight, Scissors, Calendar, Instagram, MessageCircle, ArrowLeft, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

export default function BranchPublicPage() {
  const params = useParams<{ branchSlug: string }>();
  const branchSlug = params.branchSlug;
  const [, navigate] = useLocation();

  const { data, isLoading, error } = (trpc as any).branches.getPublic.useQuery(
    { slug: branchSlug ?? "" },
    { enabled: !!branchSlug }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="text-sm">Carregando filial...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-center px-4">
          <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-xl font-semibold">Filial não encontrada</h1>
          <p className="text-sm text-muted-foreground max-w-xs">
            Esta filial não existe ou está temporariamente desativada.
          </p>
        </div>
      </div>
    );
  }

  const { branch, parent } = data;

  return <BranchBookingView branch={branch} parent={parent} navigate={navigate} />;
}

// ── Componente interno de agendamento da filial ──────────────────────────────
function BranchBookingView({
  branch,
  parent,
  navigate,
}: {
  branch: any;
  parent: any;
  navigate: (path: string) => void;
}) {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  // Usa o salonId do salão-sede para buscar serviços, profissionais e avaliações
  const parentSalonId = branch.parentSalonId;
  // Stable query inputs — objetos literais inline causam re-fetches infinitos
  const branchServicesInput = useMemo(() => ({ salonId: parentSalonId }), [parentSalonId]);
  const branchProfsInput = useMemo(() => ({ salonId: parentSalonId }), [parentSalonId]);
  const branchReviewsInput = useMemo(() => ({ salonId: parentSalonId }), [parentSalonId]);
  const branchStatsInput = useMemo(() => ({ salonId: parentSalonId }), [parentSalonId]);

  const { data: services } = trpc.services.list.useQuery(
    branchServicesInput,
    { enabled: !!parentSalonId }
  );

  const { data: professionals } = trpc.professionals.list.useQuery(
    branchProfsInput,
    { enabled: !!parentSalonId }
  );

  const { data: reviews } = trpc.reviews.listPublic.useQuery(
    branchReviewsInput,
    { enabled: !!parentSalonId }
  );

  const { data: reviewStats } = trpc.reviews.getStats.useQuery(
    branchStatsInput,
    { enabled: !!parentSalonId }
  );

  const categories = ["all", ...Array.from(new Set((services ?? []).map((s: any) => s.category).filter(Boolean)))];
  const filteredServices = (services ?? []).filter(
    (s: any) => s.isActive && (selectedCategory === "all" || s.category === selectedCategory)
  );

  const salonColor = "#C9A96E";

  return (
    <div className="min-h-screen bg-background">
      {/* Banner da filial */}
      <div
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, #0a0a0f 0%, #1a1208 60%, #0d0a05 100%)` }}
      >
        <div className="absolute inset-0 opacity-20"
          style={{ background: `radial-gradient(circle at 30% 50%, ${salonColor}40 0%, transparent 60%)` }}
        />
        <div className="relative max-w-4xl mx-auto px-4 py-10">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </button>
          <div className="flex items-start gap-5">
            {branch.logoUrl ? (
              <img
                src={branch.logoUrl}
                alt={branch.name}
                className="w-20 h-20 rounded-2xl object-cover border-2 border-white/10 shadow-xl"
              />
            ) : (
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center border-2 border-white/10 shadow-xl"
                style={{ background: `${salonColor}20` }}
              >
                <Building2 className="w-10 h-10" style={{ color: salonColor }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-xs border-white/20 text-white/50">
                  Filial de {parent?.name}
                </Badge>
              </div>
              <h1 className="text-3xl font-bold text-white">{branch.name}</h1>
              {branch.description && (
                <p className="text-white/60 mt-1 text-sm">{branch.description}</p>
              )}
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-white/50">
                {branch.address && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" />
                    {branch.address}
                  </span>
                )}
                {branch.phone && (
                  <span className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    {branch.phone}
                  </span>
                )}
                {branch.email && (
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    {branch.email}
                  </span>
                )}
              </div>
              {reviewStats && (reviewStats as any).totalReviews > 0 && (
                <div className="flex items-center gap-1.5 mt-2">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-white font-semibold">{Number((reviewStats as any).avgRating).toFixed(1)}</span>
                  <span className="text-white/40 text-sm">({(reviewStats as any).totalReviews} avaliações)</span>
                </div>
              )}
            </div>
          </div>

          {/* CTA */}
          <div className="flex gap-3 mt-6">
            <Button
              className="gap-2 font-semibold"
              style={{ background: salonColor, color: "#0a0a0f" }}
              onClick={() => navigate(`/booking?salon=${parentSalonId}&branchId=${branch.id}&branchName=${encodeURIComponent(branch.name)}`)}
            >
              <Calendar className="w-4 h-4" />
              Agendar agora
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 text-white hover:bg-white/10"
              onClick={() => navigate(`/filial/${branch.slug}/loja`)}
            >
              <ShoppingBag className="w-4 h-4" />
              Ver loja
            </Button>
          </div>
        </div>
      </div>

      {/* Serviços */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-xl font-bold mb-4">Serviços disponíveis</h2>

        {/* Filtro de categorias */}
        {categories.length > 1 && (
          <div className="flex gap-2 flex-wrap mb-5">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === cat
                    ? "text-black"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                style={selectedCategory === cat ? { background: salonColor } : {}}
              >
                {cat === "all" ? "Todos" : cat}
              </button>
            ))}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {filteredServices.map((service: any) => (
            <Card
              key={service.id}
              className="cursor-pointer hover:border-primary/50 transition-colors overflow-hidden"
              onClick={() => navigate(`/booking?salon=${parentSalonId}&serviceId=${service.id}&branchId=${branch.id}&branchName=${encodeURIComponent(branch.name)}`)}
            >
              {service.imageUrl && (
                <div className="h-24 w-full overflow-hidden">
                  <img src={service.imageUrl} alt={service.name} className="w-full h-full object-cover" />
                </div>
              )}
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{service.name}</p>
                  {service.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{service.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {service.durationMinutes} min
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-bold text-primary">
                    {Number(service.price) > 0
                      ? `R$ ${Number(service.price).toFixed(2).replace(".", ",")}`
                      : "Consultar"}
                  </p>
                  <ChevronRight className="w-4 h-4 text-muted-foreground ml-auto mt-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredServices.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Scissors className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>Nenhum serviço encontrado.</p>
          </div>
        )}
      </div>

      {/* Profissionais */}
      {professionals && (professionals as any[]).length > 0 && (
        <div className="max-w-4xl mx-auto px-4 pb-8">
          <Separator className="mb-8" />
          <h2 className="text-xl font-bold mb-4">Nossa equipe</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {(professionals as any[]).filter((p) => p.isActive).map((prof) => (
              <Card key={prof.id}>
                <CardContent className="p-4 text-center">
                  {prof.avatarUrl ? (
                    <img src={prof.avatarUrl} alt={prof.name} className="w-14 h-14 rounded-full object-cover mx-auto mb-2 border-2 border-border" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                      <Scissors className="w-6 h-6 text-primary" />
                    </div>
                  )}
                  <p className="font-semibold text-sm">{prof.name}</p>
                  {prof.bio && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{prof.bio}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Avaliações */}
      {reviews && (reviews as any[]).length > 0 && (
        <div className="max-w-4xl mx-auto px-4 pb-12">
          <Separator className="mb-8" />
          <h2 className="text-xl font-bold mb-4">Avaliações</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {(reviews as any[]).slice(0, 4).map((review) => (
              <Card key={review.id}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3.5 h-3.5 ${i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-muted"}`}
                      />
                    ))}
                  </div>
                  {review.comment && <p className="text-sm text-muted-foreground line-clamp-3">{review.comment}</p>}
                  <p className="text-xs text-muted-foreground mt-2 font-medium">{review.clientName ?? "Cliente"}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
